import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Sparkles, Compass, MessageCircle, ArrowRight, Calendar, Map, Plane } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import GuestStartModal from "../components/GuestStartModal";

const STEPS = [
  { icon: Calendar, n: "01", title: "Enter your birth details", desc: "Date, time and place - takes 60 seconds. No password." },
  { icon: Map, n: "02", title: "See your lines on the map", desc: "Nine planetary lines drawn across the globe, plus a 1-100 score for any city you type." },
  { icon: Plane, n: "03", title: "Go where you shine", desc: "Find your Money, Love and Healing cities, share your postcard, and book the trip." },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [startOpen, setStartOpen] = useState(false);

  React.useEffect(() => {
    if (!loading && user) {
      navigate(user.natal ? "/dashboard" : "/onboarding", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const startCta = (
    <Button
      data-testid="hero-cta-start"
      onClick={() => setStartOpen(true)}
      size="lg"
      className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-[88px] sm:h-28 px-14 sm:px-20 text-2xl sm:text-4xl font-heading font-bold tracking-wide max-w-full shadow-[0_0_60px_rgba(200,169,110,0.45)] hover:shadow-[0_0_90px_rgba(200,169,110,0.6)] hover:-translate-y-0.5 transition-all"
    >
      Reveal My Blueprint <ArrowRight className="ml-3 sm:ml-4 shrink-0" size={34} />
    </Button>
  );

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#15111A]">
      <div className="starfield" />
      <div className="noise" />
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 50% 15%, rgba(74,51,86,0.35), transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(200,169,110,0.14), transparent 60%)"
      }} />

      <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-8 pt-8">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full glass-gold flex items-center justify-center">
              <Sparkles size={18} className="text-[#C8A96E]" />
            </div>
            <span className="font-heading text-2xl tracking-tight" data-testid="brand-title">StarGrid</span>
          </div>
          <Button data-testid="nav-login-btn" onClick={handleLogin} variant="outline" className="border-[#C8A96E]/50 text-[#C8A96E] hover:bg-[#C8A96E]/10 bg-transparent rounded-none">Sign In</Button>
        </nav>

        {/* Hero — single focus, one CTA */}
        <section className="mt-20 sm:mt-28 text-center max-w-3xl mx-auto">
          <p className="text-xs sm:text-sm uppercase tracking-[0.35em] text-[#A89C98] mb-6">Astrocartography · Your chart, mapped on Earth</p>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
            Find the places on Earth<br />
            <span className="text-[#C8A96E]">where you shine.</span>
          </h1>
          <p className="mt-8 text-[#E8DFD3] text-base sm:text-lg leading-relaxed" data-testid="landing-hero-copy">
            Astrocartography takes the exact sky at the moment you were born and projects it onto the map - every planet was rising, setting or overhead somewhere, and along those lines its energy is amplified in your life.
            StarGrid draws your lines, scores any city from 1 to 100, and shows you where love, money and healing come easiest.
          </p>
          <div className="mt-10 flex flex-col items-center gap-4">
            {startCta}
            <p className="text-xs sm:text-sm text-[#A89C98]" data-testid="hero-cta-hint">
              Free · 60 seconds · just your birth date, time &amp; place · 2 free healing PDFs on signup
            </p>
          </div>
        </section>

        {/* 3 simple steps */}
        <section className="mt-24 sm:mt-28" data-testid="landing-steps">
          <p className="text-center text-[10px] uppercase tracking-[0.35em] text-[#C8A96E] mb-8">How it works</p>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="glass rounded-2xl p-6 relative" data-testid={`landing-step-${s.n}`}>
                <div className="font-mono-scanner text-4xl text-[#C8A96E]/30 absolute right-5 top-4">{s.n}</div>
                <div className="w-11 h-11 rounded-md glass-gold flex items-center justify-center text-[#C8A96E] mb-4"><s.icon size={20} /></div>
                <h3 className="font-heading text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-[#A89C98] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Proof / what you get */}
        <section className="mt-24 grid md:grid-cols-3 gap-5">
          <Feature icon={<Compass />} title="Destiny Scanner" desc="Type any city. Get a 1-100 resonance score and see which of your lines run through it." />
          <Feature icon={<MessageCircle />} title="Cosmic Community" desc="Live hubs for every city, DMs with compatibility badges, and a weekly heatmap of where travelers score highest." />
          <Feature icon={<Sparkles />} title="Natal Blueprint" desc="A 21-page personalized report - Sun, Moon & Rising, nine planetary lines, destined cities, wellness journey and 12-month outlook." />
        </section>

        {/* Repeat CTA at the bottom of the funnel */}
        <section className="mt-24 text-center">
          <h2 className="font-heading text-base md:text-lg text-[#A89C98] mb-6">Ready to see your map?</h2>
          {startCta}
        </section>

        <footer className="mt-24 py-10 text-sm text-[#A89C98] text-center border-t border-white/5" data-testid="footer">
          © StarGrid · Aligned by the stars ·{" "}
          <a href="https://www.astro.com/astrology/in_astrogeography_e.htm" target="_blank" rel="noopener noreferrer" data-testid="hero-cta-learn" className="text-[#C8A96E] hover:text-white">Learn the craft</a>
        </footer>
      </div>

      <GuestStartModal open={startOpen} onOpenChange={setStartOpen} onSignIn={handleLogin} />
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="glass rounded-2xl p-6 hover:-translate-y-1 transition-transform">
      <div className="w-10 h-10 rounded-md glass-gold flex items-center justify-center text-[#C8A96E] mb-4">{icon}</div>
      <h3 className="font-heading text-xl mb-2">{title}</h3>
      <p className="text-sm text-[#A89C98] leading-relaxed">{desc}</p>
    </div>
  );
}
